import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, listUsers } from '../../../services';
import { deactivateUser, deleteUserPermanently } from '../../../services/usersAdminApi';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './UsersList.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faBan, faPlus, faEye, faUser } from '@fortawesome/free-solid-svg-icons';

function UsersList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden, setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState({ email: '', role: '', idtenant: '' });
  const [showConfirmHardDelete, setShowConfirmHardDelete] = useState(false);
  const [userToHardDelete, setUserToHardDelete] = useState(null);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  const history = useHistory();

  const getRoleDisplay = (role) => {
    if (role === 'root') return 'Técnico';
    if (role === 'admin') return 'Administrativo';
    return 'Usuário';
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'root') return 'badge-role-root';
    if (role === 'admin') return 'badge-role-admin';
    return 'badge-role-user';
  };

  const getEndpointsviewerDisplay = (listEndpoint) => {
    if (listEndpoint === 'all') return 'Todos';
    if (listEndpoint === 'none') return 'Nenhum';
    if (Array.isArray(listEndpoint)) return listEndpoint.join(', ');
    if (typeof listEndpoint === 'string' && listEndpoint.includes(',')) {
      return listEndpoint.split(',').join(', ');
    }
    return listEndpoint || 'Nenhum';
  };

  const getQueuesviewerDisplay = (listQueues) => {
    if (listQueues === 'all') return 'Todas';
    if (listQueues === 'none') return 'Nenhuma';
    if (Array.isArray(listQueues)) return listQueues.join(', ');
    if (typeof listQueues === 'string' && listQueues.includes(',')) {
      return listQueues.split(',').join(', ');
    }
    return listQueues || 'Nenhuma';
  };

  const fetchUsers = async () => {
    try {
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      if (!dataUser || !dataUser.user) {
        setLoading(false);
        return;
      }

      const { role, idtenant, email } = dataUser.user;
      setCurrentUser({ email: email || '', role: role || '', idtenant: idtenant || '' });
      let usersList = await listUsers();
      
      if (!usersList || !Array.isArray(usersList)) {
        setUsers([]);
        setLoading(false);
        return;
      }

      if (role === 'root' && idtenant === 'vgs') {
        usersList = usersList.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase().trim();
          const nameB = (b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'pt-BR');
        });
        setUsers(usersList);
      } else {
        usersList = usersList.filter((user) => {
          if (Array.isArray(idtenant)) {
            return idtenant.includes(user.idtenant);
          }
          return user.idtenant === idtenant;
        });
        usersList = usersList.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase().trim();
          const nameB = (b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'pt-BR');
        });
        setUsers(usersList);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setUsers([]);
      setLoading(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    
    const { user: { role } } = dataUser;
    if (role !== 'root') return history.push('/home');

    await fetchUsers();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, []);

  const handleEdit = (user) => {
    setItemsSelectedToEdit(user.email);
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/usuarios/editar');
  };

  const handleView = (user) => {
    setItemsSelectedToEdit(user);
    history.push('/config/usuarios/visualizar');
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setShowConfirmDelete(false);
    const emailToDelete = userToDelete.email;
    setUserToDelete(null);
    setDeleting(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      const result = await deactivateUser({ email: emailToDelete });

      if (result && result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: `Usuário ${emailToDelete} desativado com sucesso!`
        });
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: (result && result.error) || 'Não foi possível desativar o usuário. Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao desativar usuário:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Erro ao desativar usuário. Tente novamente.'
      });
    } finally {
      setDeleting(false);
      await fetchUsers();
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setUserToDelete(null);
  };

  const isMasterRoot = currentUser.role === 'root' && currentUser.idtenant === 'vgs';

  const isOwnAccount = (user) =>
    !!currentUser.email && String(user.email || '').toLowerCase() === currentUser.email.toLowerCase();

  const hardDeleteConfirmed = !!userToHardDelete
    && hardDeleteConfirmText.trim().toLowerCase() === String(userToHardDelete.email || '').toLowerCase();

  const confirmHardDelete = (user) => {
    setUserToHardDelete(user);
    setHardDeleteConfirmText('');
    setShowConfirmHardDelete(true);
  };

  const cancelHardDelete = () => {
    setShowConfirmHardDelete(false);
    setUserToHardDelete(null);
    setHardDeleteConfirmText('');
  };

  const handleHardDelete = async () => {
    if (!hardDeleteConfirmed) return;

    const emailToDelete = userToHardDelete.email;
    cancelHardDelete();
    setDeleting(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      const result = await deleteUserPermanently({ email: emailToDelete });
      if (result && result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: `Usuário ${emailToDelete} excluído definitivamente.`
        });
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: (result && result.error) || 'Não foi possível excluir definitivamente o usuário.'
        });
      }
    } finally {
      setDeleting(false);
      await fetchUsers();
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'badge-active' : 'badge-inactive';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const filteredUsers = users.filter((user) => {
    const searchText = filterText.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchText)) ||
      (user.email && user.email.toLowerCase().includes(searchText)) ||
      (user.endpoint && user.endpoint.toString().includes(searchText)) ||
      (user.role && getRoleDisplay(user.role).toLowerCase().includes(searchText)) ||
      (user.idtenant && user.idtenant.toLowerCase().includes(searchText))
    );
  });

  if (loading) return <Loading />;

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="users-title-wrapper">
          <svg className="users-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <div>
            <h1 className="users-title">Usuários</h1>
            <p className="users-subtitle">Gerenciar usuários do sistema</p>
          </div>
        </div>
        <div className="users-actions">
          <Link to="/config/usuarios/novo" className="btn-new-user">
            <FontAwesomeIcon icon={faPlus} />
            Novo Usuário
          </Link>
        </div>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                ×
              </button>
            </div>
            <div className="alert-body">
              {alert.message}
            </div>
          </div>
        </div>
      )}

      <div className="filter-section">
        <div className="filter-input-wrapper">
          <svg className="filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="filter-input"
            placeholder="Filtrar por nome, e-mail, ramal, tipo ou tenant..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <button className="filter-clear" onClick={() => setFilterText('')}>
              ×
            </button>
          )}
        </div>
        <div className="filter-results">
          Exibindo {filteredUsers.length} de {users.length} usuário(s)
        </div>
      </div>

      {users.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <div className="empty-state-title">Nenhum usuário encontrado</div>
          <p className="empty-state-text">
            Clique em "Novo Usuário" para criar seu primeiro usuário.
          </p>
        </div>
      ) : (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead className="users-table-header">
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Ramal</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Tenant</th>
                <th>Visão Ramais</th>
                <th>Visão Filas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.email}>
                  <td>
                    <strong>{user.name}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className="user-badge badge-endpoint">
                      {user.endpoint || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`user-badge ${getRoleBadgeClass(user.role)}`}>
                      {getRoleDisplay(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`user-badge ${getStatusColor(user.active)}`}>
                      {getStatusText(user.active)}
                    </span>
                  </td>
                  <td>
                    <span className="user-badge badge-tenant">
                      {user.idtenant || 'N/A'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {getEndpointsviewerDisplay(user.endpointsviewer)}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {getQueuesviewerDisplay(user.queuesviewer)}
                  </td>
                  <td>
                    <div className="users-actions-cell">
                      <button
                        className="btn-user-action btn-view-user"
                        onClick={() => handleView(user)}
                        title="Visualizar"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="btn-user-action btn-edit-user"
                        onClick={() => handleEdit(user)}
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className="btn-user-action btn-delete-user"
                        onClick={() => confirmDelete(user)}
                        title="Desativar"
                        disabled={deleting}
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </button>
                      {isMasterRoot && (
                        <button
                          className="btn-user-action btn-delete-user"
                          onClick={() => confirmHardDelete(user)}
                          title={isOwnAccount(user) ? 'Você não pode excluir a própria conta' : 'Excluir definitivamente'}
                          disabled={deleting || isOwnAccount(user)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {showConfirmDelete && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-card">
            <header className="delete-modal-header">
              <p className="delete-modal-title">Confirmar Desativação</p>
              <button 
                className="modal-close-btn" 
                aria-label="close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </header>
            <section className="delete-modal-body">
              <p>
                Tem certeza que deseja desativar o usuário{' '}
                <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                Ele não poderá mais entrar no sistema. O cadastro é mantido e o e-mail continua reservado.
              </p>
            </section>
            <footer className="delete-modal-footer">
              <button 
                className="btn-modal btn-modal-cancel"
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal btn-modal-delete"
                onClick={handleDelete}
              >
                Sim, Desativar
              </button>
            </footer>
          </div>
        </div>
      )}

      {showConfirmHardDelete && userToHardDelete && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-card">
            <header className="delete-modal-header">
              <p className="delete-modal-title">Excluir definitivamente</p>
              <button
                className="modal-close-btn"
                aria-label="close"
                onClick={cancelHardDelete}
              >
                ×
              </button>
            </header>
            <section className="delete-modal-body">
              <p>
                O usuário <strong>{userToHardDelete.name}</strong> ({userToHardDelete.email}) será removido de forma
                permanente. Uma cópia do cadastro, sem a senha, fica guardada para histórico, e o e-mail ficará livre
                para um novo cadastro.
              </p>
              <div className="delete-modal-warning">
                <strong>Esta ação não pode ser desfeita.</strong>
              </div>
              <label htmlFor="hard-delete-confirm">Digite o e-mail do usuário para confirmar</label>
              <input
                id="hard-delete-confirm"
                type="text"
                className="filter-input"
                autoComplete="off"
                value={hardDeleteConfirmText}
                onChange={(e) => setHardDeleteConfirmText(e.target.value)}
              />
            </section>
            <footer className="delete-modal-footer">
              <button
                className="btn-modal btn-modal-cancel"
                onClick={cancelHardDelete}
              >
                Cancelar
              </button>
              <button
                className="btn-modal btn-modal-delete"
                onClick={handleHardDelete}
                disabled={!hardDeleteConfirmed}
              >
                Sim, Excluir definitivamente
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersList;
